import PageHero from "@/components/site/PageHero";
import BookingWidget from "@/components/site/BookingWidget";
import { Mail, MapPin, Phone, ArrowRight, CalendarClock, ShieldCheck, MessageSquare } from "lucide-react";
import { COMPANY } from "@/lib/content";

const WHAT_TO_EXPECT = [
  {
    icon: CalendarClock,
    title: "30-minute focused call",
    copy: "A confidential conversation about your organization, current HR posture, and where a partnership could add value.",
  },
  {
    icon: ShieldCheck,
    title: "No sales pressure",
    copy: "If we're not the right fit, we'll say so — and often point you to someone who is.",
  },
  {
    icon: MessageSquare,
    title: "Clear next steps",
    copy: "You'll leave the call with a written summary of what we discussed and a scoped path forward if you want one.",
  },
];

export default function ContactPage() {
  return (
    <div data-testid="contact-page">
      <PageHero
        eyebrow="Book a Consultation"
        title="Book your free HR consultation."
        subtitle="Pick a time that works for you. A senior HR advisor from Groman Associates will meet you on the call — no gatekeepers, no sales development reps."
        breadcrumbs={[{ label: "Home", to: "/" }, { label: "Contact" }]}
      />

      {/* Booking flow */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-24 grid lg:grid-cols-12 gap-12">
        <aside className="lg:col-span-4 space-y-10">
          <div>
            <div className="overline mb-4">What to expect</div>
            <ul className="space-y-6">
              {WHAT_TO_EXPECT.map((it) => (
                <li key={it.title} className="flex gap-4 items-start">
                  <div className="w-10 h-10 border border-gray-200 grid place-items-center shrink-0">
                    <it.icon className="w-4 h-4 text-gold" />
                  </div>
                  <div>
                    <div className="font-heading font-medium text-charcoal">{it.title}</div>
                    <p className="text-sm text-charcoal-soft mt-1 leading-relaxed">{it.copy}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div className="border border-gray-200 p-6 bg-offwhite">
            <div className="overline mb-3">Response Times</div>
            <ul className="space-y-3 text-sm text-charcoal">
              <li className="flex justify-between border-b border-gray-200 pb-3"><span>New Consultations</span><span className="font-mono text-charcoal-soft">1 business day</span></li>
              <li className="flex justify-between border-b border-gray-200 pb-3"><span>RFP / GovCon Inquiries</span><span className="font-mono text-charcoal-soft">2 business days</span></li>
              <li className="flex justify-between"><span>Media &amp; Press</span><span className="font-mono text-charcoal-soft">3 business days</span></li>
            </ul>
          </div>

          <div>
            <div className="overline mb-4">Prefer to reach out directly</div>
            <ul className="space-y-4 text-sm">
              <li>
                <a href={`mailto:${COMPANY.email}`} className="flex items-center gap-3 hover:text-gold transition-colors" data-testid="contact-email-link">
                  <Mail className="w-4 h-4 text-gold" />
                  <span className="text-charcoal font-medium">{COMPANY.email}</span>
                </a>
              </li>
              <li>
                <a href={`tel:${COMPANY.phone.replace(/\D/g, "")}`} className="flex items-center gap-3 hover:text-gold transition-colors" data-testid="contact-phone-link">
                  <Phone className="w-4 h-4 text-gold" />
                  <span className="text-charcoal font-medium">{COMPANY.phone}</span>
                </a>
              </li>
              <li className="flex items-center gap-3">
                <MapPin className="w-4 h-4 text-gold" />
                <span className="text-charcoal">{COMPANY.address}</span>
              </li>
            </ul>
          </div>

          <div className="bg-charcoal text-white p-6">
            <div className="overline text-gold mb-3">Government Contracting?</div>
            <p className="text-sm text-white/75 leading-relaxed">Use our dedicated RFP inquiry form for solicitations, capability statement requests, and past performance references.</p>
            <a href="/government-contracting#rfp-form" data-testid="contact-govcon-link" className="mt-5 inline-flex items-center gap-2 text-gold text-sm font-medium">
              Submit RFP Inquiry <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </aside>

        <div className="lg:col-span-8">
          <BookingWidget />
        </div>
      </section>
    </div>
  );
}
